import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    FormControlLabel,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Appearance, StripeElementsOptions, StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { useDispatch, useSelector } from 'react-redux';
import { useStripePromise } from '../../hook/useStripePromise';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ADD_CARD_MODAL_Z_INDEX } from '../../constants/subscriptionModalZIndex';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { addPaymentMethod, createPaymentIntent, getPaymentMethods } from '../../redux/payment/action';
import { userPaymentMethodsSelector } from '../../redux/payment/selector';
import { userConfigurationSelector } from '../../selectors/selectors';
import { updateUserConfig } from '../../redux/configuration/action';
import { SITE_URL } from '../../configurations/api/url';
import { IPaymentCard } from '../../types/global/generic.types';
import PaymentCard from '../PaymentCard/PaymentCard';
import { filterPaymentCardsForRail } from '../../utils/subscriptionRailCards';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_ON_PRIMARY_FIXED,
    BRAND_OUTLINE_VARIANT,
    BRAND_PRIMARY_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../../pages/BrandDashboard/brandDashboardTokens';

const brandStripeAppearance: Appearance = {
    theme: 'night',
    variables: {
        colorPrimary: BRAND_LUXE_PRIMARY,
        colorBackground: 'rgba(38, 38, 38, 0.55)',
        colorText: '#ffffff',
        colorTextSecondary: '#adaaaa',
        colorTextPlaceholder: '#525252',
        colorDanger: '#ff7351',
        fontFamily: 'Manrope, system-ui, sans-serif',
        fontSizeBase: '15px',
        borderRadius: '12px',
        spacingUnit: '4px',
    },
    rules: {
        '.Input': {
            backgroundColor: 'rgba(38, 38, 38, 0.4)',
            border: '1px solid rgba(72, 72, 71, 0.35)',
            boxShadow: 'none',
            padding: '14px 16px',
        },
        '.Input:focus': {
            backgroundColor: 'rgba(38, 38, 38, 0.6)',
            border: '1px solid rgba(255, 201, 101, 0.4)',
            boxShadow: '0 0 0 1px rgba(255, 201, 101, 0.2)',
        },
        '.Label': {
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            fontSize: '11px',
            fontWeight: '700',
            color: '#adaaaa',
            marginBottom: '8px',
        },
    },
};

const continueBtnSx = {
    py: 2,
    mt: 0.5,
    borderRadius: 999,
    textTransform: 'none' as const,
    fontFamily: FONT_ENUM.MAIN_BOLD,
    fontWeight: 800,
    fontSize: 17,
    color: BRAND_ON_PRIMARY_FIXED,
    background: `linear-gradient(90deg, ${BRAND_LUXE_PRIMARY} 0%, ${BRAND_PRIMARY_CONTAINER} 100%)`,
    boxShadow: '0 4px 20px rgba(254, 183, 0, 0.3)',
    '&:hover': {
        background: `linear-gradient(90deg, ${BRAND_LUXE_PRIMARY} 0%, ${BRAND_PRIMARY_CONTAINER} 100%)`,
        boxShadow: '0 6px 24px rgba(254, 183, 0, 0.38)',
    },
    '&.Mui-disabled': {
        color: 'rgba(57, 39, 0, 0.45)',
        background: 'rgba(255, 201, 101, 0.35)',
    },
};

function stripePmId(card: IPaymentCard | null | undefined): string | null {
    const pm = card?.payment_method;
    return pm && String(pm).startsWith('pm_') ? String(pm) : null;
}

export interface IPaymentModalProps {
    open: boolean;
    onClose: () => void;
    amount?: number;
    /** Called when user picks a saved card or finishes adding a new one. */
    onCardReady?: (card: IPaymentCard) => void;
    title?: string;
}

const PaymentModal: React.FC<IPaymentModalProps> = ({ open, onClose, onCardReady, title = 'Payment method' }) => {
    const dispatch = useDispatch();
    const stripePromise = useStripePromise(null, { enabled: open });
    const paymentMethods = useSelector(userPaymentMethodsSelector);
    const userConfig = useSelector(userConfigurationSelector);
    const [saveForFuture, setSaveForFuture] = useState(true);
    const [mode, setMode] = useState<'saved' | 'new'>('saved');
    const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const stripeCards = useMemo(
        () => filterPaymentCardsForRail(paymentMethods, 'stripe').filter(c => Boolean(stripePmId(c))),
        [paymentMethods],
    );
    const defaultCardId = userConfig?.default_payment_method || null;

    useEffect(() => {
        if (!open) return;
        dispatch(getPaymentMethods());
    }, [open, dispatch]);

    useEffect(() => {
        if (!open) return;
        if (stripeCards.length === 0) {
            setMode('new');
            setSelectedCardId(null);
            return;
        }
        setMode('saved');
        const preferred = stripeCards.find(c => c._id === defaultCardId) || stripeCards[0];
        setSelectedCardId(preferred?._id ?? null);
    }, [open, stripeCards, defaultCardId]);

    const options: StripeElementsOptions = useMemo(
        () => ({
            mode: 'setup',
            currency: 'usd',
            setup_future_usage: saveForFuture ? 'off_session' : 'on_session',
            appearance: brandStripeAppearance,
        }),
        [saveForFuture],
    );

    const selectedCard = stripeCards.find(c => c._id === selectedCardId) || null;

    const confirmSavedCard = () => {
        if (!selectedCard) {
            showToastMessage('Select a card to continue.', 'error');
            return;
        }
        if (selectedCard._id !== defaultCardId) {
            dispatch(updateUserConfig({ payload: { default_payment_method: selectedCard._id } }));
        }
        onCardReady?.(selectedCard);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            sx={{ zIndex: ADD_CARD_MODAL_Z_INDEX }}
            PaperProps={{
                sx: {
                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                    backgroundImage: 'none',
                    borderRadius: '2rem',
                    border: `1px solid ${BRAND_OUTLINE_VARIANT}44`,
                    boxShadow: '0 25px 50px rgba(0,0,0,0.55)',
                    overflow: 'hidden',
                    maxWidth: 520,
                },
            }}>
            <Box sx={{ position: 'relative', p: { xs: 3, sm: 4 } }}>
                <IconButton
                    onClick={onClose}
                    aria-label="Close"
                    sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        color: 'rgba(255,255,255,0.55)',
                        '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.06)' },
                    }}>
                    <CloseIcon />
                </IconButton>

                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: { xs: 24, sm: 28 },
                        fontWeight: 800,
                        color: '#fff',
                        letterSpacing: '-0.02em',
                        lineHeight: 1.2,
                        mb: 1.5,
                        pr: 4,
                    }}>
                    {title}
                </Typography>

                {mode === 'new' ? (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={saveForFuture}
                                onChange={e => setSaveForFuture(e.target.checked)}
                                icon={
                                    <Box
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            border: `2px solid ${BRAND_OUTLINE_VARIANT}`,
                                            borderRadius: 1,
                                        }}
                                    />
                                }
                                checkedIcon={
                                    <Box
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            bgcolor: BRAND_LUXE_PRIMARY,
                                            borderRadius: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}>
                                        <CheckIcon sx={{ fontSize: 16, color: BRAND_ON_PRIMARY_FIXED }} />
                                    </Box>
                                }
                                sx={{ p: 0.5 }}
                            />
                        }
                        label="Save card for future use"
                        sx={{
                            m: 0,
                            mb: 2,
                            '& .MuiFormControlLabel-label': {
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 14,
                                color: '#adaaaa',
                            },
                        }}
                    />
                ) : (
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 14,
                            color: '#adaaaa',
                            lineHeight: 1.5,
                            mb: 2,
                        }}>
                        Use your main card on file, or add a new one.
                    </Typography>
                )}

                {!stripePromise ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress sx={{ color: BRAND_LUXE_PRIMARY }} />
                    </Box>
                ) : mode === 'saved' && stripeCards.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                                color: '#adaaaa',
                            }}>
                            Cards on file
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {stripeCards.map(card => {
                                const isMain =
                                    card._id === defaultCardId ||
                                    (defaultCardId == null && card._id === stripeCards[0]?._id);
                                const isSelected = card._id === selectedCardId;
                                return (
                                    <Box
                                        key={card._id}
                                        onClick={() => setSelectedCardId(card._id)}
                                        sx={{
                                            cursor: 'pointer',
                                            borderRadius: 2,
                                            p: 1,
                                            border: isSelected
                                                ? `1px solid ${BRAND_LUXE_PRIMARY}`
                                                : '1px solid rgba(255,255,255,0.1)',
                                            bgcolor: isSelected ? `${BRAND_LUXE_PRIMARY}14` : 'transparent',
                                        }}>
                                        {isMain ? (
                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    color: BRAND_LUXE_PRIMARY,
                                                    letterSpacing: '0.06em',
                                                    textTransform: 'uppercase',
                                                    mb: 0.5,
                                                    px: 0.5,
                                                }}>
                                                Main card
                                            </Typography>
                                        ) : null}
                                        <PaymentCard card={card} isModalMode cardSize="28px" isSelected={isSelected} />
                                    </Box>
                                );
                            })}
                        </Box>

                        <Button
                            onClick={() => setMode('new')}
                            sx={{
                                alignSelf: 'flex-start',
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                color: BRAND_LUXE_PRIMARY,
                                px: 0,
                                minWidth: 0,
                                '&:hover': { bgcolor: 'transparent', opacity: 0.85 },
                            }}>
                            + Add a new card
                        </Button>

                        <Button
                            onClick={confirmSavedCard}
                            disabled={!selectedCard}
                            fullWidth
                            endIcon={<ArrowForwardIcon />}
                            sx={continueBtnSx}>
                            Continue with this card
                        </Button>
                    </Box>
                ) : (
                    <Elements stripe={stripePromise} options={options} key={String(saveForFuture)}>
                        <AddCardForm
                            loading={loading}
                            setLoading={setLoading}
                            onBackToSaved={stripeCards.length > 0 ? () => setMode('saved') : undefined}
                            onSuccess={card => {
                                onCardReady?.(card);
                                onClose();
                            }}
                        />
                    </Elements>
                )}
            </Box>
        </Dialog>
    );
};

function AddCardForm({
    loading,
    setLoading,
    onBackToSaved,
    onSuccess,
}: {
    loading: boolean;
    setLoading: (v: boolean) => void;
    onBackToSaved?: () => void;
    onSuccess: (card: IPaymentCard) => void;
}) {
    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const [name, setName] = useState('');
    const [paymentComplete, setPaymentComplete] = useState(false);

    const canSubmit = Boolean(stripe && name.trim() && paymentComplete && !loading);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements || !canSubmit) return;
        setLoading(true);
        try {
            const secret = (await dispatchPromise(
                createPaymentIntent({
                    currency: 'usd',
                    payment_method_type: 'card',
                    amount: 100,
                }),
            )) as string;

            const { error: submitError } = await elements.submit();
            if (submitError) throw new Error(submitError.message);

            const { error, setupIntent } = await stripe.confirmSetup({
                elements,
                clientSecret: secret,
                confirmParams: {
                    return_url: SITE_URL,
                    payment_method_data: { billing_details: { name: name.trim() } },
                },
                redirect: 'if_required',
            });
            if (error) throw new Error(error.message);
            const pm = setupIntent?.payment_method;
            if (!pm || typeof pm !== 'string') throw new Error('No payment method created');

            const added = (await dispatchPromise(
                addPaymentMethod({
                    payment_method: pm,
                    fullName: name.trim(),
                    intent: setupIntent,
                }),
            )) as IPaymentCard;

            if (added?._id) {
                dispatch(updateUserConfig({ payload: { default_payment_method: added._id } }));
            }
            dispatch(getPaymentMethods());
            onSuccess(added);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to add payment method';
            showToastMessage(message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {onBackToSaved ? (
                <Button
                    onClick={onBackToSaved}
                    sx={{
                        alignSelf: 'flex-start',
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        color: BRAND_LUXE_PRIMARY,
                        px: 0,
                        minWidth: 0,
                        '&:hover': { bgcolor: 'transparent', opacity: 0.85 },
                    }}>
                    ← Use a card on file
                </Button>
            ) : null}

            <Box>
                <Typography
                    sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: '#adaaaa',
                        mb: 1,
                    }}>
                    Name on card
                </Typography>
                <TextField
                    fullWidth
                    hiddenLabel
                    placeholder="Name on card"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            bgcolor: 'rgba(38, 38, 38, 0.4)',
                            borderRadius: 2,
                            color: '#fff',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                        },
                        '& .MuiInputBase-input': { fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 15 },
                    }}
                />
            </Box>

            <PaymentElement
                options={{ layout: 'tabs' }}
                onChange={(event: StripePaymentElementChangeEvent) => setPaymentComplete(event.complete)}
            />

            <Typography sx={{ fontSize: 13, color: '#adaaaa', lineHeight: 1.6, textAlign: 'center', px: 1 }}>
                By providing your card information, you allow Thspian Inc to charge your card for future payments in
                accordance with their terms.
            </Typography>

            <Button
                type="submit"
                disabled={!canSubmit}
                fullWidth
                endIcon={!loading ? <ArrowForwardIcon /> : undefined}
                sx={continueBtnSx}>
                {loading ? <CircularProgress size={22} sx={{ color: BRAND_ON_PRIMARY_FIXED }} /> : 'Continue'}
            </Button>
        </Box>
    );
}

export default PaymentModal;
